import { logStartUsage, logStartGreyError } from 'owa-analytics-start';
import { getSerializationIssue } from './serialization/getSerializationIssue';
import { serialize } from './serialization/serialize';
import type { SerializationIssue } from './types/SerializationTypes';
import { traverseObjectDeep } from './utils/traverseObjectDeep';
import { getSerializationError } from './serialization/getSerializationError';
import type { ExtendedWorkerOptions } from 'owa-analytics-types';

export function postMessageWrapper(
    _this: MessagePort | Worker,
    pPostMessage: MessagePort['postMessage'],
    message: any,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    options?: any,
    workerName?: ExtendedWorkerOptions['name']
): void {
    const STACK_SIZE_LIMIT = Error.stackTraceLimit;
    // If available, increase the stack trace limit from 10 to 50 se we can see the repeating pattern
    if (Error.stackTraceLimit) {
        Error.stackTraceLimit = 50;
    }

    try {
        pPostMessage.call(_this, message, options);
    } catch (error) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error'.
        // @ts-expect-error
        if (isDataCloneError(error) && message) {
            const allSerializationIssues = findAllSerializationErrors(message);

            const { retried, retriedFailed } = retryForAnalytics(
                // Errors resulting from enabling useUnknownInCatchVariables
                // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error'.
                // @ts-expect-error
                error,
                _this,
                pPostMessage,
                message,
                options,
                workerName
            );

            const count = allSerializationIssues.length;
            const issue = count > 0 ? allSerializationIssues[0] : null;

            const errorWithDetails = getSerializationError(
                // Errors resulting from enabling useUnknownInCatchVariables
                // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
                // @ts-expect-error
                error,
                issue,
                retried,
                retriedFailed,
                count
            );

            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logStartGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logStartGreyError('SerializationError_Cloning', undefined, errorWithDetails, {
                diagnostic: errorWithDetails?.diagnosticInfo,
            });
        } else {
            // Log other types of errors with postMessage
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
            // @ts-expect-error
            const errorWithDetails = getSerializationError(error, null, false, false);

            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logStartGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logStartGreyError('SerializationError_Other', undefined, errorWithDetails, {
                diagnostic: errorWithDetails?.diagnosticInfo,
            });
        }
    }

    // Reset to default limit
    if (Error.stackTraceLimit) {
        Error.stackTraceLimit = STACK_SIZE_LIMIT;
    }
}

function retryForAnalytics(
    error: Error,
    _this: MessagePort | Worker,
    pPostMessage: MessagePort['postMessage'],
    message: any,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    options?: any,
    workerName?: ExtendedWorkerOptions['name']
): {
    retried: boolean;
    retriedFailed: boolean;
} {
    let retried = false;
    let retriedFailed = false;

    /**
     * We only retry for the Analytics Web Worker because we do not
     * know what format is expected by other workers.
     */
    if (workerName === 'AnalyticsWorker') {
        // Try calling post message again with the serialized message.
        try {
            retried = true;
            message = serialize(message);
            pPostMessage.call(_this, message, options);
        } catch (retryError) {
            retriedFailed = true;

            // Only log an issue when we get two different cloning errors.
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2339: Property 'message' does not exist on type '{}'.
            // @ts-expect-error
            if (error?.message !== retryError?.message) {
                /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
                 * BASELINE: DO NOT COPY/PASTE!
                 *	> Argument at index 1 for logStartUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
                logStartUsage('SerializationRetryFailed', undefined, {
                    error1: error?.message,
                    // Errors resulting from enabling useUnknownInCatchVariables
                    // TS2339: Property 'message' does not exist on type '{}'.
                    // @ts-expect-error
                    error2: retryError?.message,
                    attemp1: error?.stack?.slice(0, 1024),
                    // Errors resulting from enabling useUnknownInCatchVariables
                    // TS2339: Property 'stack' does not exist on type '{}'.
                    // @ts-expect-error
                    attemp2: retryError?.stack?.slice(0, 1024),
                });
            }
        }
    }

    return {
        retried,
        retriedFailed,
    };
}

function isDataCloneError(error: Error) {
    return (
        error?.name === 'DataCloneError' ||
        error?.message?.includes("Failed to execute 'postMessage") ||
        error?.message?.includes('Data cannot be cloned')
    );
}

export function findAllSerializationErrors(value: any): SerializationIssue[] {
    let issues: SerializationIssue[] = [];

    // Check for known serialization issues
    if (typeof value === 'object') {
        issues = traverseObjectDeep<SerializationIssue>(value, checkKnowSerializationIssue);
    }

    return issues;
}

function checkKnowSerializationIssue(
    parent: any,
    key: string | number,
    path: string
): SerializationIssue | null {
    const issue = getSerializationIssue(parent, key);

    if (issue) {
        issue.key = key;
        issue.path = path;
    }

    return issue;
}
